import { parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { type TimeRange } from './schemas/time-range';

// eslint-disable-next-line @typescript-eslint/ban-types
type _AnyStringWithAutocompletion = string & {};

export type Timezone =
  | 'UTC'
  | 'America/Anchorage'
  | 'America/Chicago'
  | 'America/Denver'
  | 'America/Los_Angeles'
  | 'America/New_York'
  | 'America/Phoenix'
  | _AnyStringWithAutocompletion;

export const UTC = 'UTC' as const;
export const FALLBACK_TIMEZONE = 'America/New_York' as const;

const DATE_FMT = 'yyyy-MM-dd';
const TIME_FMT = 'HH:mm:ss';
const TIMESTAMP_FMT = `${DATE_FMT} ${TIME_FMT}`;

/**
 * Create a Date object suitable for use as a plain time value. The date
 * component and any unspecified time components are zeroed.
 */
export const newTime = (hours = 0, minutes = 0, seconds = 0, ms = 0) =>
  new Date(new Date(0).setHours(hours, minutes, seconds, ms));

/**
 * Parses a time from the format of a Postgres time value.
 */
export const parseTime = (time: string): Date => {
  return parse(time, TIME_FMT, new Date(0));
};

/**
 * Parses a date from the format of a Postgres date value.
 */
export const parseDate = (date: string): Date => {
  return parse(date, DATE_FMT, new Date(0));
};

/**
 * Parses a date and time from the format of a Postgres timestamp value.
 */
export const parseTimestamp = (timestamp: string): Date => {
  return parse(timestamp, TIMESTAMP_FMT, new Date(0));
};

/**
 * Helper function to combine a date and time and parse the result.
 */
export const parseDateTime = (date: string, time: string): Date => {
  return parseTimestamp(`${date} ${time}`);
};

/**
 * Serializes the time component of a Date object to a string form
 * suitable for use as a Postgres `time` value.
 */
export const serializeTime = (time: Date, timezone: Timezone): string =>
  formatInTimeZone(time, timezone, TIME_FMT);

/**
 * Serializes the date component of a Date object to a string form
 * suitable for use as a Postgres `date` value.
 */
export const serializeDate = (date: Date, timezone: Timezone): string =>
  formatInTimeZone(date, timezone, DATE_FMT);

/**
 * Serializes the date and time components of a Date object to a string
 * form suitable for use as a Postgres `timestamp` value.
 */
export const serializeTimestamp = (dateTime: Date, timezone: Timezone): string =>
  `${serializeDate(dateTime, timezone)} ${serializeTime(dateTime, timezone)}`;

/**
 * Get the current system timezone.
 */
export const currentTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Extracts the time from a date and converts it to milliseconds since
 * the zero hour.
 */
export const timeToMs = (date: Date) =>
  date.getHours() * 60 * 60 * 1000 +
  date.getMinutes() * 60 * 1000 +
  date.getSeconds() * 1000 +
  date.getMilliseconds();

/**
 * Check if any time ranges overlap.
 */
export const timeRangesOverlap = (ranges: TimeRange[]) => {
  const sortedRanges = [...ranges].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  for (let i = 0; i < sortedRanges.length - 1; i++) {
    if (sortedRanges[i].endTime >= sortedRanges[i + 1].startTime) return true;
  }
  return false;
};

export const formatHourLabel = (hour: number) => {
  if (hour === 0) return '12a';
  if (hour < 12) return `${hour}a`;
  if (hour === 12) return '12p';
  return `${hour - 12}p`;
};
